import React, { useState, useEffect} from 'react';
import { Dropdown, Space } from 'antd';
import { CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons';
import {
    AddProductButton,
    AddProductButtonContainer,
    AddProductMenu,
    ProductsActionImage,
} from './ProductsAction.styles';
import { useModalState } from '../../../hooks/modalState';
import { useAppSelector, useAppDispatch } from '../../../hooks/redux';
import { useHistory } from 'react-router-dom';
import { AddAndDeleteProductsAction } from '../../../models/product/enums';
import AddFromAnotherSite from './AddFromAnotherSiteModal/AddFromAnotherSiteModal';
import AddFromAProjectModal from './AddFromAProjectModal/AddFromAProjectModal';
import ProductsModal from '../../productsModal/ProductsModal';
import { ProjectPermissionTypes } from '../../../models/permission/enums';
import { usePermissions } from '../../../hooks/permissions';
import { SubscriptionTypes } from '../../../models/subscription/enums';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { tryRefreshSession } from '../../../helpers/request';
import { getAccessToken, getRefreshToken, setAccountTypeCookie, setCookies } from '../../../redux/persistedState';
import { Product } from '../../../models/product/types';
import {
    importProductsFromProjectLibraryAction,
    deleteMultipleProductsAction
} from '../../../redux/actions/productActions';
import { ImportProductsFromProjectLibraryResponse } from '../../../redux/actions/productActions/types';
import { ActionResponse } from '../../../models/actionResponse/types';
import DeleteModal from '../../common/deleteModal/DeleteModal';
import AddFromMfrChoiceTemplateModal from './AddFromMfrChoiceTemplateModal/AddFromMfrChoiceTemplateModal';

interface Props {
    showActions?: boolean;
    projectId?: number;
    projectRole?: ProjectPermissionTypes;
    selectedProducts: Product[];
    copyProductsTo?: string;
    handleDeleteMultipleProducts: (products: Product[]) => void;
}

const ProductsAction: React.FC<Props> = ({
    showActions,
    projectId,
    projectRole,
    selectedProducts,
    copyProductsTo,
    handleDeleteMultipleProducts
}) => {
    const dispatch = useAppDispatch();
    const selector = useAppSelector((state) => state);
    const history = useHistory();
    const productsModal = useModalState();
    const menuModal = useModalState();
    const projectsModal = useModalState();
    const mfrChoiceModal = useModalState();
    const deleteModal = useModalState();
    const { authReducer } = selector;
    const { getProjectPermission, getAccountSubscription } = usePermissions();
    const [openModals, setOpenModals] = useState({
        addFromAnotherSite: false,
    });
    const [selectionButtonDisabled, setSelectionButtonDisabled] = useState<boolean>(true);
    const [pending, setPending] = useState<boolean>(false);

    useEffect(() => {
        if (selectedProducts) {
            if (selectedProducts.length > 0) {
                setSelectionButtonDisabled(false);
            } else {
                setSelectionButtonDisabled(true);
            }
        }
    }, [selectedProducts]);


    const getSearchNotInTcUrlParams = () => {
        const userId = authReducer.user?.id;
        const organizationId = authReducer.user?.organization?.id;
        return `project=${projectId}&userId=${userId}&organizationId=${organizationId}&return=${window.location.href}`;
    }

    const handleMenuSelect = async (data: { key: string }) => {
        menuModal.onClose();
        switch (data.key) {
            case AddAndDeleteProductsAction.Search:
                if (process.env.NODE_ENV === 'development') {
                    history.push(`/search?project=${projectId}`);
                }
                else {
                    await tryRefreshSession();
                    setCookies(getAccessToken(), getRefreshToken());
                    setAccountTypeCookie(authReducer.user?.accountRole ?? '');
                    window.location.href = `${process.env.REACT_APP_TC_URL}/search?project=${projectId}`;
                }
                break;
            case AddAndDeleteProductsAction.YourFavorites:
                history.push(`/my-favorites/${projectId}`);
                break;
            case AddAndDeleteProductsAction.OfficeFavorite:
                history.push(`/favorites/${projectId}`);
                break;
            case AddAndDeleteProductsAction.ProjectLibraryImport:
                projectsModal.onOpen();
                // Previous flow, uncomment if needed
                // history.push(`/project-library/0/${projectId}`);
                break;
            case AddAndDeleteProductsAction.ManufacturerChoiceTemplate:
                mfrChoiceModal.onOpen();
                break;
            case AddAndDeleteProductsAction.SearchNotInTc:
                window.location.href = `${process.env.REACT_APP_TC_URL}/project-builder/add-product?${getSearchNotInTcUrlParams()}`;
                break;
            case AddAndDeleteProductsAction.DeleteProducts:
                deleteModal.onOpen();
                break;
            default:
                break;
        }
    };

    const handleCloseModal = (data: { key: string }) => {
        setOpenModals({ ...openModals, [data.key]: !openModals[data.key] });
    };

    const handleCopyProducts = async () => {
        const selectedProductsIds = selectedProducts.map((p) => p.productId);

        if (projectId && copyProductsTo) {
            const { payload } = (await dispatch(
                importProductsFromProjectLibraryAction({
                    toProjectId: parseInt(copyProductsTo),
                    fromProjectId: projectId,
                    selectedProductsIds: selectedProductsIds
                }),
            )) as ActionResponse<ImportProductsFromProjectLibraryResponse>;

            if (payload?.data) {
                /* Ideally histroy.push for SPA but
                since components from ProjectInfor.tsx tightly coupled
                will require to trade off with page relaod
                TODO: Decouple components in ProjectInfo.tsx,
                to make it work with SPA navigation withour page reload
                */
                window.location.href = `/project-info/${copyProductsTo}/0/2`;
            }
        }
    };

    const actionMenu = (organizationName: string): Array<ItemType> => {
        const menu: Array<ItemType> = [
            {
                key: AddAndDeleteProductsAction.Search,
                label: 'Add from Transparency Catalog',
            },
            {
                key: AddAndDeleteProductsAction.YourFavorites,
                label: 'Add from my Product Library',
            },
            {
                key: AddAndDeleteProductsAction.ProjectLibraryImport,
                label: 'Add from a Project',
            },
            {
                key: AddAndDeleteProductsAction.ManufacturerChoiceTemplate,
                label: 'Add from Manufacturer\'s Choice Template',
            },
            { type: 'divider' },
            {
                key: AddAndDeleteProductsAction.SearchNotInTc,
                label: 'Add product not in Transparency Catalog',
            },
            { type: 'divider' },
            {
                key: AddAndDeleteProductsAction.DeleteProducts,
                label: 'Delete products',
            },
        ];
        if (getAccountSubscription() === SubscriptionTypes.OFFICE) {
            menu.splice(1, 0, {
                key: AddAndDeleteProductsAction.OfficeFavorite,
                label: `Add from ${organizationName} Product Library`,
            });
        }
        return menu;
    };

    const deleteSelectedProducts = async () => {
        setPending(true);
        const selectedProductsIds = selectedProducts.map((p) => p.productId);
        const selectedProjectProductsIds = selectedProducts.map((p) => p.id);
        if (projectId) {
            const { payload } = await dispatch(
                deleteMultipleProductsAction({
                    productIds: selectedProductsIds,
                    projectId: projectId,
                    projectProductIds: selectedProjectProductsIds
                }),
            );
            if (payload) {
                handleDeleteMultipleProducts(selectedProducts);
                setPending(false);
                deleteModal.onClose();
            }
        }
    };

    return (
        <Space>
            {showActions && (
                <ProductsActionImage src="src/assets/images/file-export.svg" preview={false} />
            )}
            {showActions && (
                <ProductsActionImage src="src/assets/images/copy-white.svg" preview={false} />
            )}
            {copyProductsTo?.length ? (
                <AddProductButtonContainer>
                    <AddProductButton id="add-products-button"
                    disabled={selectionButtonDisabled}
                    onClick={handleCopyProducts}
                    className='add-selections-button'>
                        Add selections to Project
                    </AddProductButton>
                </AddProductButtonContainer>
            ) : getProjectPermission(
                [ProjectPermissionTypes.PROJECT_ADMIN, ProjectPermissionTypes.OWNER, ProjectPermissionTypes.EDITOR],
                projectRole,
            ) && (
                <Dropdown
                    menu={{
                        items: actionMenu(authReducer.user?.organization.name || ''),
                        onClick: handleMenuSelect,
                        style: { top: -36 },
                    }}
                    placement="bottomLeft"
                    trigger={['click']}
                    onOpenChange={menuModal.onToggle}
                >
                    <AddProductButtonContainer>
                        <AddProductButton id="add-products-button">Add & delete products</AddProductButton>
                        <AddProductMenu id="add-products-menu">
                            {menuModal.isOpen ? (
                                <CaretUpOutlined style={{ color: 'white' }} />
                            ) : (
                                <CaretDownOutlined style={{ color: 'white' }} />
                            )}
                        </AddProductMenu>
                    </AddProductButtonContainer>
                </Dropdown>
            )}
            {openModals.addFromAnotherSite && (
                <AddFromAnotherSite
                    isOpen={openModals.addFromAnotherSite}
                    closeModal={handleCloseModal}
                />
            )}
            {productsModal.isOpen && (
                <ProductsModal
                    projectId={projectId}
                    isOpen={productsModal.isOpen}
                    closeModal={productsModal.onClose}
                    setProductsPackageId={() => {}}
                ></ProductsModal>
            )}
            <AddFromAProjectModal
                isOpen={projectsModal.isOpen}
                closeModal={projectsModal.onClose}
                projectId={projectId}>
            </AddFromAProjectModal>
            {mfrChoiceModal.isOpen && (
                <AddFromMfrChoiceTemplateModal
                    isOpen={mfrChoiceModal.isOpen}
                    closeModal={mfrChoiceModal.onClose}
                    projectId={projectId}
                />
            )}
            <DeleteModal
                isOpen={deleteModal.isOpen}
                closeModal={deleteModal.onClose}
                title='selected products'
                text={`${selectedProducts.length} product${selectedProducts.length > 1 ? 's' : ''}`}
                icon=""
                reload={deleteSelectedProducts}
                pending={pending}
            ></DeleteModal>
        </Space>
    );
};

export default ProductsAction;
